import { TouchEmulator } from "../action/TouchEmulator.js";
import { console } from "../core/Console.js";
import { Player } from "../core/Player.js";
import { Signal } from "../core/Signal.js";
import { Tooltip } from "../display/Tooltip.js";
import { Calc } from "../math/Calc.js";
import { DebugViewItem, DebugViewItemOptions } from "./DebugViewItem.js";
import { DebugViewWindow, DebugViewWindowOptions } from "./DebugViewWindow.js";
import { object } from "./ObjectUtil.js";

interface DebugViewOptions {
    visible?: boolean;
    collapsed?: boolean;
}

class DebugView {
    public readonly onVisibilityChange = Signal.create<[DebugView]>();

    public readonly element: HTMLDivElement;

    private _toggle: HTMLDivElement;
    private _list: HTMLDivElement;
    private _items: { [uid: string]: DebugViewItem } = {};
    private _windows: DebugViewWindow[] = [];
    private _tooltip: Tooltip;

    constructor(options: DebugViewOptions) {
        console.log("[eventfive.wordpress-plugin] [DebugView]");

        let collapsed = true;
        try {
            collapsed = (localStorage.getItem("e5-debug-collapsed") || "on") === "on";
        } catch (error) {
        }

        options = object.merge({
            visible: false,
            collapsed: collapsed
        }, options);

        this.element = document.createElement("div");
        this.element.classList.add("e5-debug-view");
        this.element.innerHTML = `
        <div class="e5-debug-view__content">
            <div class="e5-debug-view__windows"></div>
            <div class="e5-debug-view__footer">
                <div class="e5-debug-view__list"></div>
            </div>
        </div>
        <div class="e5-debug-view__toggle">
            <div class="e5-debug-view__toggle-icon fal fa-chevron-left"></div>
        </div>
        `;

        this._toggle = this.element.querySelector(".e5-debug-view__toggle");
        this._toggle.addEventListener("click",
            () => {
                this.collapsed = !this.collapsed;
            }
        );
        this._list = this.element.querySelector(".e5-debug-view__list");

        this._tooltip = new Tooltip({
            scope: this.element,
            attribute: "data-debug-tooltip",
            className: "e5-debug-view__tooltip"
        });

        this.collapsed = options.collapsed;
        this.visible = options.visible;
    }

    public get collapsed(): boolean {
        return this.element.classList.contains("e5-debug-view--collapsed");
    }

    public set collapsed(value: boolean) {
        if (this.collapsed === value) {
            return;
        }
        this.element.classList.toggle("e5-debug-view--collapsed", value);
        try {
            localStorage.setItem("e5-debug-collapsed", value ? "on" : "off");
        } catch (error) {

        }
        this.onVisibilityChange.dispatch(this);
    }

    public get visible(): boolean {
        return Boolean(this.element.parentElement);
    }

    public set visible(value: boolean) {
        if (this.visible === value) {
            return;
        }
        if (value) {
            document.body.appendChild(this.element);
        } else {
            this.element.remove();
        }

        this.onVisibilityChange.dispatch(this);
    }

    public add(options: DebugViewItemOptions): DebugViewItem {
        if (this.get(options.uid)) {
            console.error("Could not add DebugItem. The uid is already in use: " + options.uid);
            return;
        }

        const item = new DebugViewItem(options);
        this._items[options.uid] = item;

        if (options.windows) {
            for (let w of options.windows) {
                const win = this.addWindow(w);
                item.bindWindow(win);
            }
        }

        this._list.appendChild(item.element);

        return item;
    }

    public addWindow(options: DebugViewWindowOptions): DebugViewWindow {
        const win = new DebugViewWindow(options);
        this._windows.push(win);
        this.element.querySelector(".e5-debug-view__windows")
            .appendChild(win.element);
        return win;
    }

    public get(uid: string): DebugViewItem {
        return this._items[uid] || null;
    }

    public active(uid: string): boolean {
        return Boolean(this.get(uid)?.active);
    }
}

export const Debug = new DebugView({
    visible: false
});

document.addEventListener("DOMContentLoaded", () => {
    Debug.add({
        uid: "show-outlines",
        localStorage: true,
        tooltip: "Show Outlines",
        icon: "fal fa-border-style-alt",
        onToggleInit: (item) => {
            document.body.classList.toggle("e5-debug__show-outlines", item.active);
        }
    });

    if (TouchEmulator.isSupported) {
        Debug.add({
            uid: "multi-touch-emulator",
            localStorage: true,
            tooltip: "Multitouch Emulator",
            toggle: true,
            icon: "fal fa-hand-pointer",
            onToggleInit: (item) => {
                TouchEmulator.enabled = item.active;
            }
        });
    }

    Debug.add({
        uid: "fps",
        tooltip: "FPS",
        windows: [
            {
                onInit: (win) => {
                    win.html = `
                <div class="debug-view-window__fps">
                    <div class="debug-view-window__fps-counter">
                        <span class="debug-view-window__fps-value">--</span>
                        <span class="debug-view-window__fps-unit">FPS</span>
                    </div>
                    <canvas class="debug-view-window__fps-canvas"></canvas>
                </div>
                `;
                    const canvas = win.element.querySelector(".debug-view-window__fps-canvas") as HTMLCanvasElement;
                    const counter = win.element.querySelector(".debug-view-window__fps-value");

                    const ctx = canvas.getContext("2d", {
                        willReadFrequently: true
                    });
                    ctx.imageSmoothingEnabled = false;
                    ctx.lineWidth = 1;


                    let value = "";
                    const updateValue = () => {
                        const v = Math.round(fps).toString();
                        if (value === v) {
                            return;
                        }
                        value = v;
                        counter.textContent = value;
                    };

                    const shift = () => {
                        ctx.globalCompositeOperation = "copy";
                        ctx.drawImage(ctx.canvas, -1, 0);
                        ctx.globalCompositeOperation = "source-over";
                    };

                    const draw = () => {
                        updateValue();

                        shift();

                        ctx.beginPath();
                        ctx.strokeStyle = "#0F0";
                        ctx.moveTo(canvas.width - 0.5, canvas.height);
                        ctx.lineTo(canvas.width - 0.5, canvas.height - canvas.height * Calc.clamp(fps / 60, 0, 1));
                        ctx.stroke();
                    };
                    let onDraw = Signal.create();
                    onDraw.throttle(draw, 100);

                    const resize = () => {
                        const newWidth = canvas.offsetWidth;
                        const newHeight = canvas.offsetHeight;
                        let data = ctx.getImageData(0, 0, canvas.width, canvas.height);
                        canvas.width = newWidth;
                        canvas.height = newHeight;
                        ctx.putImageData(data, newWidth - data.width, newHeight - data.height);
                    };

                    win.onResizeWidth.add(() => {
                        resize();
                    });

                    resize();

                    let fps = 0;
                    let count = 0;
                    const times: number[] = [];
                    Player.onTick.add(() => {
                        const now = performance.now();
                        while (times.length > 0 && times[0] <= now - 1000) {
                            times.shift();
                        }
                        times.push(now);
                        fps = times.length;
                        if (count > 80) {
                            onDraw.dispatch();
                        } else {
                            count++;
                        }
                    });
                }
            }
        ],
        icon: "fal fa-gauge"
    });

    if (window.performance?.getEntriesByType) {
        Debug.add({
            uid: "profile",
            icon: "fal fa-stopwatch",
            localStorage: true,
            tooltip: "Profile time",
            onToggleInit: (item) => {
                if (!item.active) {
                    item.label = "";
                    return;
                }

                var nav = window.performance.getEntriesByType("navigation");
                if (nav && nav.length > 0) {
                    var v: any = nav[0];
                    var val = ((v.responseStart - v.requestStart) / 1000);
                    item.label = isNaN(val) ? '?' : val.toFixed(2);
                }
            }
        });
    }
});